import { Service, inject } from '@angular/core';
import type {
  IBotonBluetooth,
  ICliente,
  IConfigPerfil,
  IDispositivoLorawan,
  IEventoGenerico,
  IGateway,
  IGrupo,
  ILuminaria,
  IModeloDispositivo,
  IPuesta,
  IQueryParam,
  IUbicacion,
  IUsuario,
} from 'modelos';

import { lineasModelo } from '@compartido/selectores/opcion-modelo';
import { PermisosService } from '@core/permisos/permisos.service';
import type { VistaFaro } from '@core/permisos/vistas';
import type {
  BuscadorGlobal,
  GrupoResultados,
  Resultado,
  TipoBuscable,
} from '@core/ui/buscador-global';
import { iconoNivel } from '@compartido/luminarias/niveles';
import { codigoDe, valoresDe } from '@compartido/mantenimiento/estados';

import { BusquedaHttp } from './busqueda.http';

/** Cuántos resultados por tipo. Más que esto no se elige de un vistazo. */
const TOPE = 5;

/** Un `.` o un `(` sin escapar rompe la búsqueda o la vuelve carísima. */
function escapar(texto: string): string {
  return texto.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** `contiene`, sin anclar: el vecino dice "Mitre", no "Av. Mitre 1200". */
function contiene(texto: string) {
  return { $regex: escapar(texto), $options: 'i' };
}

/** Un campo por el que se busca: la ruta en Mongo y cómo se le dice en pantalla. */
type Campo = readonly [ruta: string, etiqueta: string];

/**
 * **Por qué se busca cada tipo.** Es la única fuente: la consulta arma su `$or`
 * con las rutas y la ayuda del buscador vacío lee las etiquetas. Agregar un
 * campo acá lo busca y lo anuncia a la vez; no hay un texto aparte que se
 * desalinee.
 */
const CAMPOS = {
  luminarias: [
    ['identificacion', 'identificación'],
    ['deveui', 'DevEUI'],
    ['direccion', 'dirección'],
  ],
  puestas: [
    ['identificacion', 'identificación'],
    ['direccion', 'dirección'],
  ],
  nodos: [
    ['name', 'nombre'],
    ['deveui', 'DevEUI'],
  ],
  grupos: [['nombre', 'nombre']],
  zonas: [['identificacion', 'nombre']],
  gateways: [
    ['nombre', 'nombre'],
    ['nombreChirpstack', 'nombre en el network server'],
    ['gatewayEui', 'EUI'],
  ],
  botones: [
    ['mac', 'MAC'],
    ['serialNumber', 'número de serie'],
  ],
  clientes: [
    ['nombre', 'nombre'],
    ['numeroCliente', 'número de cliente'],
  ],
  usuarios: [
    ['usuario', 'cuenta'],
    ['datosPersonales.nombre', 'nombre'],
    ['datosPersonales.email', 'correo electrónico'],
  ],
  perfiles: [
    ['nombre', 'nombre'],
    ['tipoConfig', 'tipo de configuración'],
  ],
  modelos: [
    ['marca', 'marca'],
    ['modelo', 'modelo'],
  ],
  solicitudes: [['valores.numeroIncidente', 'número de seguimiento']],
} as const satisfies Record<string, readonly Campo[]>;

/** El mismo texto contra los campos del tipo. */
function enAlguno(texto: string, campos: readonly Campo[]) {
  const regex = contiene(texto);
  return { $or: campos.map(([ruta]) => ({ [ruta]: regex })) };
}

/** «por nombre, DevEUI o dirección». */
function porCampos(campos: readonly Campo[]): string {
  const etiquetas = campos.map(([, etiqueta]) => etiqueta);
  const ultima = etiquetas.pop();
  return `por ${etiquetas.length ? `${etiquetas.join(', ')} o ${ultima}` : ultima}`;
}

/** Un tipo de cosa que el buscador sabe encontrar. */
interface Tipo {
  clave: string;
  etiqueta: string;
  /** El mismo del menú y de los listados (`ui/layout/menu.ts`). */
  icono: string;
  /** Por qué campos se busca (`CAMPOS`). */
  campos: readonly Campo[];
  /** Si el operador puede ver este tipo. Sin permiso, ni se pregunta. */
  visible: () => boolean;
  consulta: (texto: string, señal?: AbortSignal) => Promise<Resultado[]>;
  /**
   * El listado con la búsqueda ya puesta, para cuando los primeros cinco no
   * alcanzan. Sólo en los listados que filtran por `?buscar=` con los mismos
   * campos: si no, "ver todos" mostraría otra cosa.
   */
  verTodos?: (texto: string) => Pick<Resultado, 'ruta' | 'queryParams'>;
}

/**
 * El buscador global.
 *
 * **Cada tipo se pide por separado y cada uno se cae solo.** Es la lección del
 * selector de entidad de IRIX: nueve consultas en un `forkJoin` sin
 * `catchError`, y un 403 de un módulo que el cliente no tiene vaciaba el
 * resultado entero. Acá cada consulta se atrapa sola y el grupo que falló se
 * marca, en vez de desaparecer como si no hubiera nada.
 *
 * **Sólo se pregunta por lo que el operador puede ver**, con las mismas
 * acciones y banderas que el menú: un tipo sin permiso no sale ni como grupo
 * fallido, porque no hay nada que el operador pueda hacer con ese aviso.
 *
 * Los `select` son mínimos a propósito y ninguna consulta pide el total: esto
 * se dispara con cada tecla y el parque tiene decenas de miles de luminarias.
 */
@Service()
export class BusquedaGlobalService implements BuscadorGlobal {
  private readonly http = inject(BusquedaHttp);
  private readonly permisos = inject(PermisosService);

  /** En el orden en que se muestran: primero lo que se busca todos los días. */
  private readonly tipos: readonly Tipo[] = [
    {
      clave: 'luminarias',
      campos: CAMPOS.luminarias,
      etiqueta: 'Luminarias',
      icono: 'lightbulb',
      visible: () => this.ve('luminarias'),
      consulta: (t, s) => this.buscarLuminarias(t, s),
    },
    {
      clave: 'puestas',
      campos: CAMPOS.puestas,
      etiqueta: 'Puestas',
      icono: iconoNivel('puesta'),
      visible: () => this.ve('puestas'),
      consulta: (t, s) => this.buscarPuestas(t, s),
      verTodos: (t) => ({ ruta: ['/inventario/puestas'], queryParams: { buscar: t } }),
    },
    {
      clave: 'nodos',
      campos: CAMPOS.nodos,
      etiqueta: 'Nodos',
      icono: 'router',
      visible: () => this.ve('nodos'),
      consulta: (t, s) => this.buscarNodos(t, s),
      verTodos: (t) => ({ ruta: ['/red/nodos'], queryParams: { buscar: t } }),
    },
    {
      clave: 'grupos-luminarias',
      campos: CAMPOS.grupos,
      etiqueta: 'Grupos de luminarias',
      icono: 'workspaces',
      visible: () => this.ve('gruposLuminarias'),
      consulta: (t, s) => this.buscarGrupos('Luminaria', t, s),
      verTodos: (t) => ({ ruta: ['/inventario/grupos/luminarias'], queryParams: { buscar: t } }),
    },
    {
      clave: 'grupos-puestas',
      campos: CAMPOS.grupos,
      etiqueta: 'Grupos de puestas',
      icono: 'hub',
      visible: () => this.ve('gruposPuestas'),
      consulta: (t, s) => this.buscarGrupos('Puesta', t, s),
      verTodos: (t) => ({ ruta: ['/inventario/grupos/puestas'], queryParams: { buscar: t } }),
    },
    {
      clave: 'zonas',
      campos: CAMPOS.zonas,
      etiqueta: 'Zonas',
      icono: 'crop_free',
      visible: () => this.ve('zonas'),
      consulta: (t, s) => this.buscarZonas(t, s),
    },
    {
      clave: 'gateways',
      campos: CAMPOS.gateways,
      etiqueta: 'Gateways',
      icono: 'settings_input_antenna',
      visible: () => this.ve('gateways'),
      consulta: (t, s) => this.buscarGateways(t, s),
      verTodos: (t) => ({ ruta: ['/red/gateways'], queryParams: { buscar: t } }),
    },
    {
      clave: 'botones',
      campos: CAMPOS.botones,
      etiqueta: 'Botones BLE',
      icono: 'touch_app',
      visible: () => this.ve('botonesBle'),
      consulta: (t, s) => this.buscarBotones(t, s),
      verTodos: (t) => ({ ruta: ['/red/botones-ble'], queryParams: { buscar: t } }),
    },
    {
      clave: 'clientes',
      campos: CAMPOS.clientes,
      etiqueta: 'Clientes',
      icono: 'business',
      visible: () => this.ve('clientes'),
      consulta: (t, s) => this.buscarClientes(t, s),
      verTodos: (t) => ({ ruta: ['/administracion/clientes'], queryParams: { buscar: t } }),
    },
    {
      clave: 'usuarios',
      campos: CAMPOS.usuarios,
      etiqueta: 'Usuarios',
      icono: 'person',
      visible: () => this.ve('usuarios'),
      consulta: (t, s) => this.buscarUsuarios(t, s),
    },
    {
      clave: 'perfiles',
      campos: CAMPOS.perfiles,
      etiqueta: 'Perfiles de configuración',
      icono: 'tune',
      visible: () => this.ve('perfiles'),
      consulta: (t, s) => this.buscarPerfiles(t, s),
      verTodos: (t) => ({ ruta: ['/operacion/perfiles'], queryParams: { buscar: t } }),
    },
    {
      clave: 'modelos-luminaria',
      campos: CAMPOS.modelos,
      etiqueta: 'Modelos de luminaria',
      icono: 'lightbulb_circle',
      visible: () => this.ve('modelosLuminaria'),
      consulta: (t, s) => this.buscarModelos('Luminaria', t, s),
      verTodos: (t) => ({ ruta: ['/catalogos/modelos-luminaria'], queryParams: { buscar: t } }),
    },
    {
      clave: 'modelos-dispositivo',
      campos: CAMPOS.modelos,
      etiqueta: 'Modelos de dispositivo',
      icono: 'memory',
      visible: () => this.ve('modelosDispositivo'),
      consulta: (t, s) => this.buscarModelos('DispositivoLorawan', t, s),
      verTodos: (t) => ({ ruta: ['/catalogos/modelos-dispositivo'], queryParams: { buscar: t } }),
    },
    {
      clave: 'servicio-tecnico',
      campos: CAMPOS.solicitudes,
      etiqueta: 'Servicio técnico',
      icono: 'construction',
      visible: () => this.ve('solicitudes'),
      consulta: (t, s) => this.buscarSolicitudes(t, s),
    },
  ];

  async buscar(texto: string, señal?: AbortSignal): Promise<GrupoResultados[]> {
    const t = texto.trim();
    if (!t) return [];

    const grupos = await Promise.all(
      this.tipos.filter((tipo) => tipo.visible()).map((tipo) => this.grupo(tipo, t, señal)),
    );

    // Un grupo vacío que no falló no ocupa lugar en la lista.
    return grupos.filter((g) => g.resultados.length || g.fallo);
  }

  /** Qué puede buscar este operador: los mismos tipos y permisos que `buscar`. */
  buscables(): TipoBuscable[] {
    return this.tipos
      .filter((tipo) => tipo.visible())
      .map(({ clave, etiqueta, icono, campos }) => ({
        clave,
        etiqueta,
        icono,
        campos: porCampos(campos),
      }));
  }

  /** Envuelve una consulta para que su fallo no se lleve puestas las otras. */
  private async grupo(tipo: Tipo, texto: string, señal?: AbortSignal): Promise<GrupoResultados> {
    const { clave, etiqueta, icono } = tipo;
    try {
      const resultados = await tipo.consulta(texto, señal);
      // "Ver todos" sólo tiene sentido si se llenó el tope: puede haber más.
      const verTodos = resultados.length >= TOPE ? tipo.verTodos?.(texto) : undefined;
      return { clave, etiqueta, icono, resultados, verTodos };
    } catch {
      return { clave, etiqueta, icono, resultados: [], fallo: true };
    }
  }

  /** La misma regla que el menú y el guard de la ruta (`VISTAS`). */
  private ve(vista: VistaFaro): boolean {
    return this.permisos.puedeVer(vista);
  }

  private async listar<T>(
    ruta: string,
    filtro: object,
    select: string,
    señal?: AbortSignal,
    sort?: string,
  ): Promise<T[]> {
    const query: IQueryParam = {
      filter: JSON.stringify(filtro),
      limit: TOPE,
      select,
      includeChildren: true,
      ...(sort ? { sort } : {}),
    };
    // Silencioso: si una colección falla, su grupo ya lo dice ("no se pudo buscar").
    const { datos } = await this.http.listar<T>(ruta, query, señal, { silencioso: true });
    return datos ?? [];
  }

  /**
   * La luminaria se busca por tres cosas, y el orden importa poco: el `$or`
   * deja que Mongo elija el índice que sirva. Lo que no se hace es traer todo y
   * filtrar acá.
   */
  private async buscarLuminarias(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<ILuminaria>(
      '/luminarias',
      enAlguno(texto, CAMPOS.luminarias),
      '_id identificacion direccion deveui',
      señal,
    );
    return datos.map((l) => ({
      titulo: l.identificacion || l.deveui || 'Sin identificación',
      detalle: [l.direccion, l.deveui].filter(Boolean).join(' · '),
      ruta: ['/inventario/luminarias', l._id ?? ''],
    }));
  }

  /** Por identificación o dirección, igual que el buscador del listado. */
  private async buscarPuestas(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IPuesta>(
      '/puestas',
      enAlguno(texto, CAMPOS.puestas),
      '_id identificacion direccion',
      señal,
    );
    return datos.map((p) => ({
      titulo: p.identificacion || 'Sin identificación',
      detalle: p.direccion,
      ruta: ['/inventario/puesta', p._id ?? ''],
    }));
  }

  /** Por nombre o DevEUI: es lo que dicta un técnico por teléfono. */
  private async buscarNodos(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IDispositivoLorawan>(
      '/dispositivoslorawan',
      enAlguno(texto, CAMPOS.nodos),
      '_id name deveui',
      señal,
    );
    return datos.map((n) => ({
      titulo: n.name || n.deveui || 'Sin nombre',
      detalle: n.name ? n.deveui : undefined,
      ruta: ['/red/nodos', n._id ?? ''],
    }));
  }

  /**
   * Un grupo de luminarias y uno de puestas son cosas distintas —agrupan
   * entidades distintas y tienen cada uno su listado—, así que van en grupos
   * de resultados separados. La categoría decide la ranura de la ruta.
   */
  private async buscarGrupos(
    categoria: 'Luminaria' | 'Puesta',
    texto: string,
    señal?: AbortSignal,
  ): Promise<Resultado[]> {
    const datos = await this.listar<IGrupo>(
      '/grupos',
      { ...enAlguno(texto, CAMPOS.grupos), categoria },
      '_id nombre',
      señal,
    );
    const ranura = categoria === 'Puesta' ? 'puestas' : 'luminarias';
    return datos.map((g) => ({
      titulo: g.nombre || 'Sin nombre',
      ruta: ['/inventario/grupos', ranura, g._id ?? ''],
    }));
  }

  /** La zona no tiene pantalla propia: se abre desde su listado. */
  private async buscarZonas(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IUbicacion>(
      '/ubicacions',
      { ...enAlguno(texto, CAMPOS.zonas), categoria: 'Luminarias' },
      '_id identificacion direccion',
      señal,
    );
    return datos.map((z) => ({
      titulo: z.identificacion || 'Sin identificación',
      detalle: z.direccion,
      ruta: ['/inventario/zonas'],
    }));
  }

  private async buscarGateways(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IGateway>(
      '/gateways',
      enAlguno(texto, CAMPOS.gateways),
      '_id nombre nombreChirpstack gatewayEui',
      señal,
    );
    return datos.map((g) => ({
      titulo: g.nombre || g.nombreChirpstack || g.gatewayEui || 'Sin nombre',
      detalle: g.gatewayEui,
      ruta: ['/red/gateways', g._id ?? ''],
    }));
  }

  /** Por MAC o número de serie, los dos que trae impresos el botón. */
  private async buscarBotones(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IBotonBluetooth>(
      '/botonesBLE',
      enAlguno(texto, CAMPOS.botones),
      '_id mac serialNumber',
      señal,
    );
    return datos.map((b) => ({
      titulo: b.mac || b.serialNumber || 'Sin MAC',
      detalle: b.mac ? b.serialNumber : undefined,
      ruta: ['/red/botones-ble', b._id ?? ''],
    }));
  }

  private async buscarClientes(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<ICliente>(
      '/clientes',
      enAlguno(texto, CAMPOS.clientes),
      '_id nombre numeroCliente tipoCliente',
      señal,
    );
    return datos.map((c) => ({
      titulo: c.nombre || 'Sin nombre',
      detalle: [c.tipoCliente, c.numeroCliente && `N.º ${c.numeroCliente}`]
        .filter(Boolean)
        .join(' · '),
      ruta: ['/administracion/clientes', c._id ?? ''],
    }));
  }

  /**
   * Por cuenta, nombre o email. **No hay pantalla de usuario**: se abre el
   * listado de permisos filtrado por la cuenta, que es donde se administra.
   */
  private async buscarUsuarios(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IUsuario>(
      '/usuarios',
      enAlguno(texto, CAMPOS.usuarios),
      '_id usuario datosPersonales.nombre datosPersonales.email',
      señal,
    );
    return datos.map((u) => {
      const nombre = u.datosPersonales?.nombre?.trim();
      return {
        titulo: nombre || u.usuario || 'Sin nombre',
        detalle: [nombre ? u.usuario : undefined, u.datosPersonales?.email]
          .filter(Boolean)
          .join(' · '),
        ruta: ['/administracion/usuarios'],
        queryParams: u.usuario ? { buscar: u.usuario } : undefined,
      };
    });
  }

  /**
   * El perfil se ve en un modal del listado: se llega con la búsqueda puesta y
   * `?abrir=` abre su visor de sólo lectura.
   */
  private async buscarPerfiles(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IConfigPerfil>(
      '/configPerfils',
      { ...enAlguno(texto, CAMPOS.perfiles), tipoEntidad: 'Luminaria' },
      '_id nombre tipoConfig',
      señal,
    );
    return datos.map((p) => ({
      titulo: p.nombre || 'Sin nombre',
      detalle: p.tipoConfig,
      ruta: ['/operacion/perfiles'],
      queryParams: conAbrir(p._id, p.nombre),
    }));
  }

  /**
   * Los dos modelos de una luminaria —el del driver y el del nodo— viven en la
   * misma colección y `tipo` los separa. Cada uno va a su listado, que con
   * `?abrir=` abre el formulario si el operador puede editarlo.
   */
  private async buscarModelos(
    tipo: 'Luminaria' | 'DispositivoLorawan',
    texto: string,
    señal?: AbortSignal,
  ): Promise<Resultado[]> {
    const datos = await this.listar<IModeloDispositivo>(
      '/modelodispositivos',
      { ...enAlguno(texto, CAMPOS.modelos), tipo },
      '_id marca modelo',
      señal,
    );
    const ruta = tipo === 'Luminaria' ? 'modelos-luminaria' : 'modelos-dispositivo';
    return datos.map((m) => ({
      ...lineasModelo(m),
      ruta: ['/catalogos', ruta],
      queryParams: conAbrir(m._id, m.modelo),
    }));
  }

  /**
   * El servicio técnico se busca por su **número de seguimiento**
   * (`valores.numeroIncidente`, el «Código» de la columna de IRIX): es lo que
   * se le dicta al vecino o a la cuadrilla. Con el mismo alcance que el listado
   * —sólo luminarias, servicio técnico y verificación—, abiertas y finalizadas,
   * la más nueva primero. Se atiende en un diálogo: `?abrir=` lo abre.
   */
  private async buscarSolicitudes(texto: string, señal?: AbortSignal): Promise<Resultado[]> {
    const datos = await this.listar<IEventoGenerico>(
      '/eventos-genericos',
      {
        tipoEvento: 'Evento Técnico Luminaria',
        filtrador: { $in: ['Servicio Técnico', 'Verificación'] },
        ...enAlguno(texto, CAMPOS.solicitudes),
      },
      // `valores` entero, como las sugerencias del listado de solicitudes.
      '_id valores estado',
      señal,
      '-fechaCreacion',
    );
    return datos.map((s) => ({
      titulo: codigoDe(s) || 'Sin código',
      detalle: [valoresDe(s).titulo, s.estado].filter(Boolean).join(' · '),
      ruta: ['/mantenimiento/solicitudes'],
      queryParams: s._id ? { abrir: s._id } : undefined,
    }));
  }
}

/**
 * Para los catálogos que se editan en un modal: la búsqueda deja el listado
 * mostrando el elegido y `abrir` abre su diálogo.
 */
function conAbrir(id: string | undefined, buscar: string | undefined) {
  const q: Record<string, string> = {};
  if (buscar) q['buscar'] = buscar;
  if (id) q['abrir'] = id;
  return Object.keys(q).length ? q : undefined;
}
